// ============================================
// Tiền tệ đang áp dụng cho màn hình (sheet 2 mục #75)
// ============================================
//
// Quy tắc chọn nguồn, theo thứ tự ưu tiên:
//   1. `currency` trên CHÍNH bản ghi (ReturnRequest, Payment, SessionReceipt…)
//      — chính xác nhất, dùng ngay khi có.
//   2. `Store.currency` của cửa hàng đang chọn — cho màn dữ liệu một cửa hàng.
//   3. `APP_CURRENCY` — chỉ khi không biết gì.
//
// Hook này lo bước 2. Bước 1 các trang tự đọc từ dữ liệu (đừng gọi hook rồi
// ghi đè currency thật của bản ghi — đó là cách dán sai ký hiệu).
//
// ⚠️ Ở chế độ "All locations" của chuỗi nhiều thị trường, KHÔNG có một tiền tệ
// nào đúng cho cả bảng. Lúc đó dùng `useIsMixedCurrencyScope()` để cảnh báo và
// tách tổng theo từng loại tiền (`groupByCurrency`), đừng lấy đại tiền tệ của
// cửa hàng đầu tiên.

import { useMemo } from 'react';
import { useStoreSelectionStore } from '../stores';
import { useStores } from './useStores';
import { extractList } from '../utils/apiList';
import { APP_CURRENCY } from '../utils/currency';

/** Tiền tệ của cửa hàng đang chọn; thiếu thì rơi về mặc định của app. */
export function useActiveCurrency(): string {
  const selectedStore = useStoreSelectionStore((s) => s.selectedStore);
  return selectedStore?.currency?.trim().toUpperCase() || APP_CURRENCY;
}

/**
 * Phạm vi đang xem có trộn nhiều loại tiền không.
 *
 * `true` khi KHÔNG chọn cửa hàng cụ thể (xem toàn chuỗi) VÀ các cửa hàng khai
 * từ hai loại tiền trở lên. Chuỗi một thị trường luôn trả `false` nên màn hình
 * không hiện cảnh báo thừa.
 */
export function useIsMixedCurrencyScope(): { isMixed: boolean; currencies: string[] } {
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);
  const { data: storesData } = useStores({ page_size: 100 } as never);

  return useMemo(() => {
    // Đã khoanh về một cửa hàng thì không thể trộn.
    if (selectedStoreId) return { isMixed: false, currencies: [] };

    const codes = new Set<string>();
    for (const store of extractList<{ currency?: string | null }>(storesData)) {
      const code = store.currency?.trim().toUpperCase();
      if (code) codes.add(code);
    }
    const currencies = [...codes];
    return { isMixed: currencies.length > 1, currencies };
  }, [selectedStoreId, storesData]);
}
